import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase/config';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { Award, CheckCircle, XCircle, Eye, AlertCircle, Calendar, Tag, Users } from 'lucide-react';

const PendingAchievementsWidget = () => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [error, setError] = useState('');
  const [mentorInfo, setMentorInfo] = useState(null);
  const [menteeCount, setMenteeCount] = useState(0);

  useEffect(() => {
    loadMentorAndAchievements();
  }, []);

  const loadMentorAndAchievements = async () => {
    setLoading(true);
    setError('');

    try {
      // Step 1: Get current user info from session or Firebase Auth
      let mentorEmail = null;
      
      // Check userSession first (for anonymous auth with username/password)
      const userSession = sessionStorage.getItem('userSession');
      if (userSession) {
        const session = JSON.parse(userSession);
        mentorEmail = session.email;
        console.log('🔐 Logged in via session:', mentorEmail);
      } else {
        // Fallback to Firebase Auth email
        const currentUser = auth.currentUser;
        if (!currentUser || !currentUser.email) {
          setError('Not logged in. Please refresh and try again.');
          setLoading(false);
          return;
        }
        mentorEmail = currentUser.email;
        console.log('🔐 Logged in via Firebase Auth:', mentorEmail);
      }

      if (!mentorEmail) {
        setError('Unable to identify user. Please log out and log in again.');
        setLoading(false);
        return;
      }

      // Step 2: Get mentor's profile data
      const mentorDoc = await getDoc(doc(db, 'users', mentorEmail));
      if (!mentorDoc.exists()) {
        setError('Mentor profile not found. Please contact administrator.');
        setLoading(false);
        return;
      }

      const mentorData = mentorDoc.data();
      setMentorInfo(mentorData);
      console.log('👨‍🏫 Mentor data:', mentorData);

      // Step 3: Check if mentor has assigned batch
      if (!mentorData.assignedBatch) {
        setError('No batch assigned to you. Please contact administrator.');
        setLoading(false);
        return;
      }

      console.log('📚 Assigned batch:', mentorData.assignedBatch);

      // Step 4: Get all students in mentor's batch
      const studentsQuery = query(
        collection(db, 'students'),
        where('batch', '==', mentorData.assignedBatch)
      );
      const studentsSnapshot = await getDocs(studentsQuery);
      
      console.log('👥 Students in batch:', studentsSnapshot.size);

      // Step 5: Extract roll numbers from mentor's mentees
      const menteeRollNumbers = studentsSnapshot.docs.map(doc => doc.data().rollNumber);
      setMenteeCount(menteeRollNumbers.length);

      if (menteeRollNumbers.length === 0) {
        setError('No students found in your assigned batch.');
        setLoading(false);
        return;
      }

      console.log('📝 Mentee roll numbers:', menteeRollNumbers);

      // Step 6: Get all pending achievements
      const achievementsQuery = query(
        collection(db, 'achievements'),
        where('status', '==', 'pending')
      );
      const achievementsSnapshot = await getDocs(achievementsQuery);
      
      console.log('🏆 Total pending achievements:', achievementsSnapshot.size);

      // Step 7: Filter achievements to show ONLY mentor's mentees
      const menteeAchievements = achievementsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(ach => menteeRollNumbers.includes(ach.rollNumber));

      console.log('✅ Filtered achievements for this mentor:', menteeAchievements.length);

      // Sort by submission date (newest first)
      menteeAchievements.sort((a, b) => {
        const aTime = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : 0;
        const bTime = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : 0;
        return bTime - aTime;
      });

      setAchievements(menteeAchievements);

    } catch (error) {
      console.error('❌ Error loading achievements:', error);
      setError('Failed to load achievements. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAchievement = async (achievementId, status, reason = null) => {
    if (!mentorInfo || !mentorInfo.email) {
      alert('Mentor information not found. Please refresh and try again.');
      return;
    }

    setVerifyingId(achievementId);
    setVerifying(true);

    try {
      // Update achievement status in Firestore
      await updateDoc(doc(db, 'achievements', achievementId), {
        status: status,
        verifiedBy: mentorInfo.email,
        verificationDate: new Date(),
        rejectionReason: reason
      });

      // Remove from local state immediately for instant UI feedback
      setAchievements(prev => prev.filter(a => a.id !== achievementId));

      // Show success message
      const message = status === 'verified' 
        ? '✅ Achievement verified successfully!' 
        : '❌ Achievement rejected successfully!';
      alert(message);

      console.log(`✅ Achievement ${achievementId} ${status} by ${mentorInfo.email}`);

    } catch (error) {
      console.error('❌ Error verifying achievement:', error);
      alert('Failed to verify achievement. Please try again.');
    } finally {
      setVerifying(false);
      setVerifyingId(null);
    }
  };

  const handleReject = (achievement) => {
    const reason = window.prompt(
      `Please provide a reason for rejecting "${achievement.title}":`,
      ''
    );
    
    if (reason && reason.trim()) {
      handleVerifyAchievement(achievement.id, 'rejected', reason.trim());
    } else if (reason !== null) {
      alert('Rejection reason is required. Achievement was not rejected.');
    }
  };

  const openDocument = async (documentUrl) => {
    if (!documentUrl) {
      alert('Document URL not found.');
      return;
    }
    
    try {
      // Open Firebase Storage URL directly
      console.log('📄 Opening document:', documentUrl);
      window.open(documentUrl, '_blank');
    } catch (error) {
      console.error('Error opening document:', error);
      alert('Failed to open document.');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'co-curricular': 'bg-blue-100 text-blue-800',
      'extra-curricular': 'bg-purple-100 text-purple-800',
      'project': 'bg-green-100 text-green-800',
      'certification': 'bg-orange-100 text-orange-800',
      'training': 'bg-pink-100 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600 font-medium">Loading achievements...</p>
        <p className="text-gray-400 text-sm mt-2">Fetching your mentees' data</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-900 font-semibold mb-1">Unable to Load Achievements</p>
            <p className="text-red-700 text-sm">{error}</p>
            <button
              onClick={loadMentorAndAchievements}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - no pending achievements
  if (achievements.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
        <p className="text-gray-500 text-lg font-medium mb-2">
          No pending verifications
        </p>
        <p className="text-gray-400 text-sm">
          All achievements from your mentees have been verified!
        </p>
        {mentorInfo && (
          <div className="mt-4 inline-block px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <span className="font-semibold">Batch {mentorInfo.assignedBatch}</span> • {menteeCount} mentees
            </p>
          </div>
        )}
      </div>
    );
  }

  // Main content - achievement cards
  return (
    <div className="space-y-4">
      {/* Achievement cards */}
      {achievements.map(achievement => (
        <div 
          key={achievement.id} 
          className="border-2 border-gray-200 rounded-lg p-5 bg-white hover:border-blue-300 transition-all shadow-sm hover:shadow-md"
        >
          {/* Student Info Header */}
          <div className="flex items-center space-x-3 mb-3 pb-3 border-b border-gray-100">
            <div className="flex items-center space-x-2">
              <span className="font-bold text-blue-600 text-lg">
                {achievement.rollNumber}
              </span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-700 font-medium">
                {achievement.studentName}
              </span>
            </div>
          </div>

          {/* Achievement Title */}
          <h3 className="font-bold text-gray-900 text-lg mb-2 leading-tight">
            {achievement.title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">
            {achievement.description}
          </p>

          {/* Metadata Row */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* Category Badge */}
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getCategoryColor(achievement.category)}`}>
              {achievement.category?.replace('-', ' ').toUpperCase()}
            </span>

            {/* Subcategory */}
            <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
              <Tag className="w-3 h-3" />
              <span>{achievement.subcategory}</span>
            </div>

            {/* Date */}
            <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(achievement.date)}</span>
            </div>
          </div>

          {/* Document View Button */}
          {achievement.documentUrl && (
            <div className="mb-4">
              <button
                onClick={() => openDocument(achievement.documentUrl)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="font-medium text-sm">View Supporting Document</span>
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t border-gray-100">
            <button
              onClick={() => handleVerifyAchievement(achievement.id, 'verified')}
              disabled={verifying && verifyingId === achievement.id}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
            >
              {verifying && verifyingId === achievement.id ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Verify Achievement</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => handleReject(achievement)}
              disabled={verifying && verifyingId === achievement.id}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
            >
              {verifying && verifyingId === achievement.id ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  <span>Reject</span>
                </>
              )}
            </button>
          </div>
        </div>
      ))}

      {/* Summary Footer */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Award className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-blue-900 font-medium">
              {achievements.length} achievement{achievements.length !== 1 ? 's' : ''} pending verification from your mentees
            </p>
            <p className="text-xs text-blue-700 mt-1 flex items-center space-x-2">
              <Users className="w-3 h-3" />
              <span>Batch: {mentorInfo?.assignedBatch} • {menteeCount} mentees • Review documents carefully before approving</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingAchievementsWidget;
